/**
 * Billing a customer, and taking the money.
 *
 * One asymmetry with vendor bills is deliberate and worth seeing on the screen.
 * A vendor bill is somebody else's claim on us and gets a three-way match; a
 * customer invoice is our claim on them and gets a checker instead, because the
 * risk is different — billing the wrong amount loses a customer, and billing
 * nothing at all loses the money.
 *
 * A receipt is not put to a checker at all. It is a fact about money that has
 * already arrived, and approving it would be approving the past.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'

interface InvoiceLine {
  line_no: number
  service_code: string | null
  description: string
  quantity: number
  rate_paise: number
  amount_paise: number
  gl_account_id: string
  is_exempt: boolean
  gst_rate_bp: number
}

interface Invoice {
  id: string
  reference: string
  party_id: string
  party_name: string
  rate_contract_id: string | null
  invoice_date: string
  due_date: string | null
  status: string
  exempt_paise: number
  taxable_paise: number
  cgst_paise: number
  sgst_paise: number
  total_paise: number
  expected_deduction_paise: number
  received_paise: number
  outstanding_paise: number
  journal_entry_id: string | null
  approval_state: string
  lines: InvoiceLine[]
}

interface Customer {
  id: string
  code: string
  name: string
  payment_terms_days: number
}

interface RevenueHead {
  id: string
  code: string
  name: string
}

interface BankAccount {
  id: string
  name: string
  kind: string
}

interface ReceiptAllocation {
  customer_invoice_id: string
  invoice_reference: string
  amount_paise: number
}

interface Receipt {
  id: string
  reference: string
  party_name: string
  received_on: string
  method: string
  bank_account_name: string
  amount_paise: number
  on_account_paise: number
  instrument_ref: string | null
  journal_entry_id: string | null
  allocations: ReceiptAllocation[]
}

interface Quote {
  service_code: string
  description: string
  rate_paise: number
  gl_account_id: string | null
  is_exempt: boolean
  gst_rate_bp: number
  contract_reference: string
}

interface DraftLine {
  description: string
  service_code: string
  amount: string
  gl_account_id: string
  is_exempt: boolean
  gst_rate_bp: string
}

const today = () => new Date().toISOString().slice(0, 10)
const BLANK = (): DraftLine => ({
  description: '',
  service_code: '',
  amount: '',
  gl_account_id: '',
  is_exempt: true,
  gst_rate_bp: '0',
})

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function Billing() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [drafting, setDrafting] = useState(false)
  const [party, setParty] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(today())
  const [lines, setLines] = useState<DraftLine[]>([BLANK()])
  const [collecting, setCollecting] = useState<Invoice | null>(null)
  const [receiptBank, setReceiptBank] = useState('')
  const [receiptDate, setReceiptDate] = useState(today())
  const [receiptAmount, setReceiptAmount] = useState('')
  const [receiptRef, setReceiptRef] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['customer-invoices'] })
    void queryClient.invalidateQueries({ queryKey: ['receipts'] })
    void queryClient.invalidateQueries({ queryKey: ['ar-ageing'] })
  }

  const invoices = useQuery({
    queryKey: ['customer-invoices'],
    queryFn: () => api.get<Invoice[]>('/receivables/invoices'),
  })
  const customers = useQuery({
    queryKey: ['ar-customers'],
    queryFn: () => api.get<Customer[]>('/receivables/customers'),
  })
  const heads = useQuery({
    queryKey: ['revenue-heads'],
    queryFn: () => api.get<RevenueHead[]>('/receivables/revenue-heads'),
    enabled: can('ar.invoice.create'),
  })
  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('ar.receipt.create'),
  })
  const receipts = useQuery({
    queryKey: ['receipts'],
    queryFn: () => api.get<Receipt[]>('/receivables/receipts'),
    enabled: can('ar.receipt.view'),
  })

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<Invoice>('/receivables/invoices', body),
    onSuccess: (made) => {
      refresh()
      setDrafting(false)
      setLines([BLANK()])
      setOpen(made.id)
    },
  })
  const submit = useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/receivables/invoices/${id}/submit`),
    onSuccess: refresh,
  })
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post<Invoice>(`/receivables/invoices/${id}/decide?approve=${approve}`),
    onSuccess: refresh,
  })
  const post = useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/receivables/invoices/${id}/post`),
    onSuccess: refresh,
  })
  const collect = useMutation({
    mutationFn: (body: unknown) => api.post<Receipt>('/receivables/receipts', body),
    onSuccess: () => {
      refresh()
      setCollecting(null)
      setReceiptAmount('')
      setReceiptRef('')
    },
  })

  /** Ask the contract what was agreed, and fill the line in with it. */
  const priceIt = async (index: number) => {
    const line = lines[index]
    if (!party || !line.service_code) return
    const quoted = await api.get<Quote | null>(
      `/receivables/quote?party_id=${party}&service_code=${encodeURIComponent(
        line.service_code,
      )}&on=${invoiceDate}`,
    )
    if (!quoted) return
    setLines((current) =>
      current.map((l, i) =>
        i === index
          ? {
              ...l,
              description: l.description || quoted.description,
              amount: (quoted.rate_paise / 100).toString(),
              gl_account_id: quoted.gl_account_id ?? l.gl_account_id,
              is_exempt: quoted.is_exempt,
              gst_rate_bp: String(quoted.gst_rate_bp),
            }
          : l,
      ),
    )
  }

  if (invoices.isLoading) return <Loading what="Loading invoices" />
  const rows = invoices.data ?? []
  const opened = rows.find((i) => i.id === open)
  const draftTotal = lines.reduce((sum, l) => sum + toPaise(l.amount), 0)

  return (
    <>
      <ErrorNote
        error={
          invoices.error ??
          create.error ??
          submit.error ??
          decide.error ??
          post.error ??
          collect.error
        }
      />

      {can('ar.invoice.create') && (
        <Card
          title="Raise an invoice"
          actions={
            <button className="small" onClick={() => setDrafting(!drafting)}>
              {drafting ? 'Cancel' : 'New invoice'}
            </button>
          }
        >
          {!drafting ? (
            <p className="faint">
              A contract fills the price in; the line keeps its own amount. A
              contract renegotiated later cannot reach back and change what was
              billed.
            </p>
          ) : (
            <>
              <div className="row">
                <Field label="Customer">
                  <select value={party} onChange={(e) => setParty(e.target.value)}>
                    <option value="">Choose…</option>
                    {(customers.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Invoice date">
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                </Field>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Description</th>
                    <th>Revenue head</th>
                    <th>GST</th>
                    <th className="num">Amount</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          value={line.service_code}
                          placeholder="CONSULT"
                          onChange={(e) =>
                            setLines((c) =>
                              c.map((l, j) =>
                                j === i ? { ...l, service_code: e.target.value } : l,
                              ),
                            )
                          }
                          onBlur={() => void priceIt(i)}
                        />
                      </td>
                      <td>
                        <input
                          value={line.description}
                          onChange={(e) =>
                            setLines((c) =>
                              c.map((l, j) =>
                                j === i ? { ...l, description: e.target.value } : l,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={line.gl_account_id}
                          onChange={(e) =>
                            setLines((c) =>
                              c.map((l, j) =>
                                j === i ? { ...l, gl_account_id: e.target.value } : l,
                              ),
                            )
                          }
                        >
                          <option value="">Choose…</option>
                          {(heads.data ?? []).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={line.is_exempt ? 'exempt' : line.gst_rate_bp}
                          onChange={(e) =>
                            setLines((c) =>
                              c.map((l, j) =>
                                j === i
                                  ? e.target.value === 'exempt'
                                    ? { ...l, is_exempt: true, gst_rate_bp: '0' }
                                    : {
                                        ...l,
                                        is_exempt: false,
                                        gst_rate_bp: e.target.value,
                                      }
                                  : l,
                              ),
                            )
                          }
                        >
                          <option value="exempt">Exempt</option>
                          <option value="500">5%</option>
                          <option value="1200">12%</option>
                          <option value="1800">18%</option>
                        </select>
                      </td>
                      <td className="num">
                        <input
                          className="num"
                          value={line.amount}
                          onChange={(e) =>
                            setLines((c) =>
                              c.map((l, j) =>
                                j === i ? { ...l, amount: e.target.value } : l,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        {lines.length > 1 && (
                          <button
                            className="small"
                            onClick={() =>
                              setLines((c) => c.filter((_, j) => j !== i))
                            }
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="row">
                <button className="small" onClick={() => setLines((c) => [...c, BLANK()])}>
                  Add a line
                </button>
                <span className="faint">
                  Lines come to {formatInr(draftTotal)} before tax
                </span>
                <button
                  disabled={!party || lines.some((l) => !l.description || !l.gl_account_id)}
                  onClick={() =>
                    create.mutate({
                      party_id: party,
                      invoice_date: invoiceDate,
                      lines: lines.map((l) => ({
                        description: l.description,
                        service_code: l.service_code || null,
                        gl_account_id: l.gl_account_id,
                        amount_paise: toPaise(l.amount),
                        is_exempt: l.is_exempt,
                        gst_rate_bp: l.is_exempt ? 0 : Number(l.gst_rate_bp),
                      })),
                    })
                  }
                >
                  Save as draft
                </button>
              </div>
              <p className="faint">
                Healthcare by a clinical establishment is exempt. Pharmacy and most
                diagnostics are not — which one a line is decides whether the
                customer is charged tax, so it is chosen here rather than guessed.
              </p>
            </>
          )}
        </Card>
      )}

      <Card title="Invoices" hint={`${rows.length}`} tight>
        {rows.length === 0 ? (
          <div className="empty">Nothing billed yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Due</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Outstanding</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <button className="small" onClick={() => setOpen(invoice.id)}>
                      {invoice.reference}
                    </button>
                  </td>
                  <td>{invoice.party_name}</td>
                  <td className="muted date">{invoice.invoice_date}</td>
                  <td className="muted date">{invoice.due_date ?? '—'}</td>
                  <td>
                    <Pill value={invoice.status} />
                  </td>
                  <td className="num">{formatInr(invoice.total_paise)}</td>
                  <td className="num">
                    {invoice.outstanding_paise ? (
                      formatInr(invoice.outstanding_paise)
                    ) : (
                      <span className="faint">settled</span>
                    )}
                  </td>
                  <td>
                    {invoice.status === 'draft' && can('ar.invoice.submit') && (
                      <button className="small" onClick={() => submit.mutate(invoice.id)}>
                        Send for checking
                      </button>
                    )}
                    {invoice.status === 'submitted' && can('ar.invoice.approve') && (
                      <>
                        <button
                          className="small"
                          onClick={() => decide.mutate({ id: invoice.id, approve: true })}
                        >
                          Approve
                        </button>
                        <button
                          className="small"
                          onClick={() => decide.mutate({ id: invoice.id, approve: false })}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {invoice.status === 'submitted' && !can('ar.invoice.approve') && (
                      <span className="faint">waiting on a checker</span>
                    )}
                    {invoice.status === 'approved' && can('ar.invoice.post') && (
                      <button className="small" onClick={() => post.mutate(invoice.id)}>
                        Post
                      </button>
                    )}
                    {invoice.status === 'posted' &&
                      invoice.outstanding_paise > 0 &&
                      can('ar.receipt.create') && (
                        <button
                          className="small"
                          onClick={() => {
                            setCollecting(invoice)
                            setReceiptAmount(
                              (invoice.outstanding_paise / 100).toString(),
                            )
                          }}
                        >
                          Record a receipt
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {opened && (
        <Card
          title={opened.reference}
          hint={opened.party_name}
          actions={
            <button className="small" onClick={() => setOpen(null)}>
              Close
            </button>
          }
          tight
        >
          <Note kind={opened.status === 'posted' ? 'ok' : 'info'}>
            {opened.approval_state}
          </Note>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Service</th>
                <th>Description</th>
                <th>Tax</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {opened.lines.map((line) => (
                <tr key={line.line_no}>
                  <td className="muted">{line.line_no}</td>
                  <td className="mono">{line.service_code ?? '—'}</td>
                  <td>{line.description}</td>
                  <td className="muted">
                    {line.is_exempt ? 'exempt' : `${line.gst_rate_bp / 100}%`}
                  </td>
                  <td className="num">{formatInr(line.amount_paise)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={4}>
                  <strong>Tax</strong>
                </td>
                <td className="num">
                  {formatInr(opened.cgst_paise + opened.sgst_paise)}
                </td>
              </tr>
              <tr>
                <td colSpan={4}>
                  <strong>Total</strong>
                </td>
                <td className="num">
                  <strong>{formatInr(opened.total_paise)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          {opened.expected_deduction_paise > 0 && (
            <Note kind="warn">
              The contract expects {formatInr(opened.expected_deduction_paise)} to be
              deducted before this is paid. A receivable that will never arrive in full
              says so from the start rather than at the moment it is written off.
            </Note>
          )}
        </Card>
      )}

      {collecting && (
        <Card
          title={`Money in against ${collecting.reference}`}
          hint={collecting.party_name}
          actions={
            <button className="small" onClick={() => setCollecting(null)}>
              Cancel
            </button>
          }
        >
          <div className="row">
            <Field label="Received on">
              <input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
              />
            </Field>
            <Field label="Into">
              <select value={receiptBank} onChange={(e) => setReceiptBank(e.target.value)}>
                <option value="">Choose…</option>
                {(banks.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Amount">
              <input
                className="num"
                value={receiptAmount}
                onChange={(e) => setReceiptAmount(e.target.value)}
              />
            </Field>
            <Field label="Reference" caption="UTR, cheque number">
              <input value={receiptRef} onChange={(e) => setReceiptRef(e.target.value)} />
            </Field>
          </div>
          <button
            disabled={!receiptBank || !toPaise(receiptAmount)}
            onClick={() =>
              collect.mutate({
                party_id: collecting.party_id,
                received_on: receiptDate,
                bank_account_id: receiptBank,
                amount_paise: toPaise(receiptAmount),
                instrument_ref: receiptRef || null,
                allocations:
                  toPaise(receiptAmount) <= collecting.outstanding_paise
                    ? [
                        {
                          customer_invoice_id: collecting.id,
                          amount_paise: toPaise(receiptAmount),
                        },
                      ]
                    : [],
              })
            }
          >
            Record it
          </button>
          <p className="faint">
            More than the bill leaves the whole receipt on account rather than
            over-settling one invoice. Money on account is a real state — forcing
            somebody to pick an invoice as the cash lands is how it gets applied to
            the wrong one.
          </p>
        </Card>
      )}

      {can('ar.receipt.view') && (
        <Card title="Money in" hint={`${(receipts.data ?? []).length}`} tight>
          {(receipts.data ?? []).length === 0 ? (
            <div className="empty">Nothing received yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Into</th>
                  <th>Settles</th>
                  <th className="num">Amount</th>
                  <th className="num">On account</th>
                </tr>
              </thead>
              <tbody>
                {(receipts.data ?? []).map((receipt) => (
                  <tr key={receipt.id}>
                    <td className="mono">{receipt.reference}</td>
                    <td>{receipt.party_name}</td>
                    <td className="muted date">{receipt.received_on}</td>
                    <td className="muted">{receipt.bank_account_name}</td>
                    <td className="muted">
                      {receipt.allocations.length === 0 ? (
                        <span className="faint">nothing yet</span>
                      ) : (
                        receipt.allocations.map((a) => a.invoice_reference).join(', ')
                      )}
                    </td>
                    <td className="num">{formatInr(receipt.amount_paise)}</td>
                    <td className="num">
                      {receipt.on_account_paise ? (
                        formatInr(receipt.on_account_paise)
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  )
}
